import { useSnackbar } from 'notistack';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Visibility } from '@/components/Visibility';
import { useCheckout } from '@/hooks/useCheckout';
import { useMyWatches } from '@/hooks/useMyWatches';
import { useUser } from '@/hooks/useUser';
import { WARRANTY_EXTENSION_VARIANT_ID } from '@/lib/const';
import { getWarranty, getWatchDetailsSecurely } from '@/lib/service/api';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { Watch } from '@/views/MyWatchesView';

import type { WarrantyExtensionSchema } from './components/WarrantyExtensionForm/schema';
import { WarrantyExtensionForm } from './components/WarrantyExtensionForm/WarrantyExtensionForm';
import type { WarrantyExtensionWatchesProps } from './components/WarrantyExtensionWatches/WarrantyExtensionWatches';
import { WarrantyExtensionWatches } from './components/WarrantyExtensionWatches/WarrantyExtensionWatches';

import * as EM from '../messages';
import * as S from './WarrantyExtensionModal.styles';

export interface WarrantyExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WarrantyExtensionModal = ({
  isOpen,
  onClose,
}: WarrantyExtensionModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const Provider = useRegionProvider();
  const {
    fetchUser,
    snapshot: { isAuthenticated },
  } = useUser();
  const {
    lineAdd,
    snapshot: { checkout },
  } = useCheckout();
  const { checkWatchExtendable, getWatchBySaleor, getWatchesList } =
    useMyWatches();

  const [isWatchesInitializing, setIsWatchesInitializing] =
    useState<boolean>(true);
  const [isUserInitialized, setIsUserInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingWatchId, setProcessingWatchId] = useState<string>();
  const [isFormView, setIsFormView] = useState(true);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [formErrorMessage, setFormErrorMessage] = useState<ReactNode>();

  const handleWarrantyExtend: WarrantyExtensionWatchesProps['onWarrantyExtend'] =
    async ({ id, sku, sn, warrantyExtensionDuration }) => {
      try {
        setProcessingWatchId(id);
        const { data } = await checkWatchExtendable(sn, checkout?.id ?? '');
        if (data && warrantyExtensionDuration) {
          await lineAdd(WARRANTY_EXTENSION_VARIANT_ID, 'ORDER', null, [
            { key: 'warranty.duration', value: warrantyExtensionDuration },
            { key: 'related.watch.model_reference', value: sku },
            { key: 'related.watch.serial_number', value: sn },
          ]);
          onClose();
        } else {
          enqueueSnackbar(
            formatMessage(EM.eventMessages.warrantyExtensionCheckFailed),
            {
              variant: 'error',
            },
          );
        }
      } finally {
        setProcessingWatchId(undefined);
      }
    };

  const handleWarrantyExtendByForm: SubmitHandler<
    WarrantyExtensionSchema
  > = async (props) => {
    try {
      setIsProcessing(true);
      const warrantyData = await getWarranty(props.sn);
      if (warrantyData.data?.sku !== props.rn)
        return setFormErrorMessage(EM.watchNotFound(onClose));
      if (warrantyData.code !== 200 && warrantyData.code !== 810) {
        const errorMessage =
          warrantyData.code === 811
            ? EM.watchNotRegisteredToYourAccount(onClose)
            : EM.watchNotFound(onClose);
        return setFormErrorMessage(errorMessage);
      }
      const res = await getWatchDetailsSecurely(
        warrantyData.data.sku,
        Provider.query.languageCode,
      );
      const product = res?.data.productVariant?.product;
      if (!product || res?.data.productVariant?.sku !== props.rn) {
        return setFormErrorMessage(EM.watchNotFound(onClose));
      }
      if (!warrantyData.data.is_bind) {
        return setFormErrorMessage(EM.watchNotRegistered(onClose));
      }
      await handleWarrantyExtend({
        id: product.id,
        sku: props.rn,
        sn: props.sn,
        warrantyExtensionDuration:
          product.warrantyExtensionDuration?.values[0]?.plainText ?? '',
      });
    } finally {
      setIsProcessing(false);
    }
  };
  useEffect(() => {
    const initializeUser = async () => {
      if (isAuthenticated === null) {
        try {
          await fetchUser();
        } finally {
          setIsUserInitialized(true);
        }
      } else {
        setIsUserInitialized(true);
      }
    };
    const initMyWatches = async () => {
      try {
        setIsWatchesInitializing(true);
        if (isAuthenticated) {
          const res = await getWatchesList();
          const myWatches = res.data as Watch[];
          if (myWatches?.length) {
            const watches = await getWatchBySaleor(myWatches, Provider);
            setWatches(watches.watches as Watch[]);
          } else {
            setWatches([]);
          }
        } else {
          setWatches([]);
        }
      } finally {
        setIsWatchesInitializing(false);
      }
    };
    initializeUser();
    initMyWatches();
  }, [isAuthenticated]);
  useEffect(() => {
    setIsFormView(!watches.length);
  }, [watches]);
  useEffect(() => {
    if (!isOpen) {
      setIsFormView(!watches.length);
      setFormErrorMessage('');
    }
  }, [isOpen]);
  return (
    <Modal
      data-testid='WarrantyExtensionModal'
      gutters={false}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={onClose}
    >
      <Modal.Header divider={true} onClose={onClose}>
        <S.Title>{formatMessage(serviceMessages.extendMyWarranty)}</S.Title>
      </Modal.Header>
      <Modal.Body>
        {!isUserInitialized || isWatchesInitializing ? (
          <Loader height={400} />
        ) : (
          <S.Body>
            <Visibility xsHidden={!isFormView}>
              <WarrantyExtensionForm
                errorMessage={formErrorMessage}
                isProcessing={isProcessing}
                onSubmit={handleWarrantyExtendByForm}
              />
            </Visibility>
            <Visibility xsHidden={isFormView}>
              <WarrantyExtensionWatches
                onNavToSearch={() => setIsFormView(true)}
                onWarrantyExtend={handleWarrantyExtend}
                processingWatchId={processingWatchId}
                watches={watches}
              />
            </Visibility>
          </S.Body>
        )}
      </Modal.Body>
    </Modal>
  );
};

WarrantyExtensionModal.displayName = 'WarrantyExtensionModal';
